import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { QC_DISTRICTS } from "./constants";
import { DateInput } from "./DateInput";
import { Row, ToggleRow, compactInputCls, compactInputMonoCls, sectionTitleCls } from "./RowLayout";

const ARTICLES = [
    { k: "art486", l: "Article 486" },
    { k: "art672", l: "Article 672" },
    { k: "art684", l: "Article 684" },
    { k: "LPJ85", l: "85.4.1 LPJ" },
    { k: "CPC278", l: "278 C.p.C." },
];

export const MegaTab = ({ readOnly, mega, setMega }) => {
    const set = (k, v) => setMega({ ...mega, [k]: v });
    const toggleDistrict = (id) => {
        const list = mega.districts || [];
        const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
        setMega({ ...mega, districts: next });
    };

    const renderToggle = (k, label, value) => (
        <ToggleRow
            key={k}
            k={k}
            label={label}
            checked={!!value}
            onChange={(v) => set(k, v)}
            disabled={readOnly}
        />
    );

    const selectedDistrictsCount = (mega.districts || []).length;

    return (
        <div className="space-y-3" data-testid="mega-form">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8">
                {/* ============= COLONNE GAUCHE — champs texte + détails ============= */}
                <div>
                    <Row label="Date inscription">
                        <div className="flex justify-end">
                            <DateInput
                                value={mega.dateinsc || ""}
                                onChange={(v) => set("dateinsc", v)}
                                disabled={readOnly}
                                className={`${compactInputMonoCls} w-[200px] text-right`}
                            />
                        </div>
                    </Row>
                    <Row label="Section barreau">
                        <div className="flex justify-end">
                            <Input
                                value={mega.sectbar || ""}
                                onChange={(e) => set("sectbar", e.target.value)}
                                disabled={readOnly}
                                className={`${compactInputCls} w-[200px] text-right`}
                                data-testid="mega-sectbar"
                            />
                        </div>
                    </Row>
                    <Row label="District de pratique">
                        <Input
                            value={mega.districthab || ""}
                            onChange={(e) => set("districthab", e.target.value.slice(0, 100))}
                            disabled={readOnly}
                            maxLength={100}
                            placeholder="Champ libre — ex. « Montréal, Laval »"
                            className={compactInputCls}
                            data-testid="mega-districthab"
                        />
                    </Row>
                    <Row label="Expérience criminel (Année début)">
                        <div className="flex justify-end">
                            <Input
                                value={mega.experience ? String(mega.experience) : ""}
                                onChange={(e) => {
                                    const digits = e.target.value.replace(/\D/g, "").slice(0, 4);
                                    set("experience", digits ? parseInt(digits, 10) : 0);
                                }}
                                disabled={readOnly}
                                maxLength={4}
                                inputMode="numeric"
                                pattern="[0-9]{4}"
                                placeholder="AAAA"
                                className={`${compactInputMonoCls} w-[200px] text-right`}
                                data-testid="mega-experience"
                            />
                        </div>
                    </Row>
                    <div className="pt-2">
                        <Label className="text-xs font-medium text-slate-700">Détails</Label>
                        <Textarea
                            value={mega.details || ""}
                            onChange={(e) => set("details", e.target.value)}
                            disabled={readOnly}
                            rows={3}
                            className="rounded-md mt-1 text-sm"
                            data-testid="mega-details"
                        />
                    </div>
                </div>

                {/* ============= COLONNE DROITE — toggles langues + articles ============= */}
                <div>
                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mb-1">Langues</div>
                    {renderToggle("francais", "Français", mega.francais)}
                    {renderToggle("anglais", "Anglais", mega.anglais)}
                    <Row label="Autres" hint="Espagnol, italien, etc.">
                        <Input
                            value={mega.autres || ""}
                            onChange={(e) => set("autres", e.target.value)}
                            disabled={readOnly}
                            placeholder="Espagnol, italien…"
                            className={compactInputCls}
                            data-testid="mega-autres"
                        />
                    </Row>

                    <div className="text-[10px] uppercase tracking-wider font-semibold text-slate-500 mt-3 mb-1">Articles habilités</div>
                    {ARTICLES.map((a) => renderToggle(a.k, a.l, mega[a.k]))}
                </div>
            </div>

            {/* ============= EN BAS — Districts + Commentaire ============= */}
            <div className="pt-1 border-t border-slate-200" />
            <div>
                <div className="flex items-center justify-between mb-2">
                    <Label className={sectionTitleCls}>
                        Districts habilités <span className="text-slate-500 font-normal normal-case">({selectedDistrictsCount})</span>
                    </Label>
                    <div className="flex items-center gap-2">
                        <Label className="text-[11px] text-slate-600">Tous</Label>
                        <ToggleRow
                            k="tous-districts"
                            label=""
                            checked={!!mega.tous_districts}
                            onChange={(v) => setMega({
                                ...mega, tous_districts: v,
                                districts: v ? QC_DISTRICTS.map((d) => d.id) : [],
                            })}
                            disabled={readOnly}
                        />
                    </div>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-x-3 gap-y-1 max-h-56 overflow-y-auto border border-slate-200 rounded-md p-2 bg-slate-50/50">
                    {QC_DISTRICTS.map((d) => (
                        <label key={d.id} className="flex items-center gap-2 text-xs cursor-pointer hover:bg-white rounded px-1.5 py-0.5">
                            <input
                                type="checkbox"
                                checked={(mega.districts || []).includes(d.id)}
                                onChange={() => toggleDistrict(d.id)}
                                disabled={readOnly}
                                data-testid={`mega-district-${d.id}`}
                            />
                            <span>{d.nom}</span>
                        </label>
                    ))}
                </div>
            </div>
            <div>
                <Label className={sectionTitleCls}>Commentaire</Label>
                <Textarea
                    value={mega.commentaire || ""}
                    onChange={(e) => set("commentaire", e.target.value)}
                    disabled={readOnly}
                    rows={3}
                    className="rounded-md mt-1 text-sm"
                    data-testid="mega-commentaire"
                />
            </div>
            {!readOnly && (
                <p className="text-[11px] text-slate-500 italic">
                    Cliquez sur « Sauvegarder » en haut à droite pour enregistrer le profil Méga.
                </p>
            )}
        </div>
    );
};
